import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { auth } from './firebase';
import { db } from './firebase';

const USERS_COLLECTION = 'sharabl-users';
const editableProfileFields = [
  'mobileNumber',
  'jobTitle',
  'bio',
  'location',
  'website',
  'organisationName',
  'organisationRole',
  'organisationWebsite',
  'organisationSize',
  'organisationAddress',
];

const serialiseUserProfile = (snapshot) => {
  const data = snapshot.data();
  return {
    id: snapshot.id,
    ...data,
    createdAt: data.createdAt?.toDate ? data.createdAt.toDate().toISOString() : data.createdAt,
    updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate().toISOString() : data.updatedAt,
  };
};

export const userService = {
  async getUserProfile(uid) {
    const snapshot = await getDoc(doc(db, USERS_COLLECTION, uid));
    return snapshot.exists() ? serialiseUserProfile(snapshot) : null;
  },

  async upsertUserProfile(firebaseUser, overrides = {}) {
    if (!firebaseUser) return null;

    const now = new Date().toISOString();
    const profileRef = doc(db, USERS_COLLECTION, firebaseUser.uid);
    const existingSnapshot = await getDoc(profileRef);
    const existingProfile = existingSnapshot.exists() ? existingSnapshot.data() : {};
    const profile = {
      uid: firebaseUser.uid,
      email: firebaseUser.email || '',
      emailLower: String(firebaseUser.email || '').trim().toLowerCase(),
      displayName: overrides.displayName ?? firebaseUser.displayName ?? '',
      photoURL: overrides.photoURL ?? firebaseUser.photoURL ?? '',
      providerId: firebaseUser.providerData?.[0]?.providerId || 'password',
      createdAt: existingProfile.createdAt || firebaseUser.metadata?.creationTime || now,
      updatedAt: serverTimestamp(),
    };

    editableProfileFields.forEach((field) => {
      if (Object.prototype.hasOwnProperty.call(overrides, field)) {
        profile[field] = overrides[field] || '';
      }
    });

    if (!existingSnapshot.exists()) {
      profile.role = 'user';
      profile.approvalStatus = 'pending';
      profile.subscriptionPlan = 'free';
      profile.subscriptionStatus = 'trialing';
      profile.featureKeys = [];
    }

    await setDoc(profileRef, profile, { merge: true });

    return {
      ...existingProfile,
      ...profile,
      id: firebaseUser.uid,
      createdAt: profile.createdAt,
      updatedAt: now,
    };
  },

  async listUsers() {
    const snapshot = await getDocs(query(collection(db, USERS_COLLECTION), orderBy('updatedAt', 'desc')));
    return snapshot.docs.map(serialiseUserProfile);
  },

  async searchUsersByEmail(emailQuery) {
    const email = String(emailQuery || '').trim().toLowerCase();
    if (email.length < 3) return [];

    const usersRef = collection(db, USERS_COLLECTION);
    const [prefixSnapshot, exactSnapshot] = await Promise.all([
      getDocs(query(
        usersRef,
        where('emailLower', '>=', email),
        where('emailLower', '<=', `${email}\uf8ff`),
        limit(8),
      )),
      getDocs(query(usersRef, where('email', '==', String(emailQuery || '').trim()), limit(1))),
    ]);

    const usersById = new Map();
    [...prefixSnapshot.docs, ...exactSnapshot.docs].forEach((snapshot) => {
      usersById.set(snapshot.id, serialiseUserProfile(snapshot));
    });

    return [...usersById.values()]
      .filter((profile) => String(profile.email || '').toLowerCase().includes(email))
      .slice(0, 8);
  },

  async updateUserAccess(uid, data) {
    if (!uid) throw new Error('Missing user id.');

    const payload = {
      role: data.role || 'user',
      approvalStatus: data.approvalStatus || 'pending',
      subscriptionPlan: data.subscriptionPlan || 'free',
      subscriptionStatus: data.subscriptionStatus || 'trialing',
      featureKeys: Array.isArray(data.featureKeys) ? data.featureKeys : [],
      updatedAt: serverTimestamp(),
      accessUpdatedAt: serverTimestamp(),
      accessUpdatedBy: auth.currentUser?.uid || '',
    };

    await setDoc(doc(db, USERS_COLLECTION, uid), payload, { merge: true });
    return {
      ...payload,
      id: uid,
      uid,
      updatedAt: new Date().toISOString(),
      accessUpdatedAt: new Date().toISOString(),
    };
  },
};
